import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, auth } from '../config/supabase';

// Get environment variables for debugging
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

interface AuthContextType {
  currentUser: User | null;
  session: Session | null;
  login: (email: string, password: string) => Promise<any>;
  register: (email: string, password: string) => Promise<any>;
  logout: () => Promise<void>;
  loginWithGoogle: () => Promise<any>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const login = async (email: string, password: string) => {
    console.log('🔵 ===== STARTING LOGIN PROCESS =====');
    console.log('🔵 Email:', email);
    console.log('🔵 Password length:', password.length);
    
    try {
      const result = await auth.signIn(email, password);
      console.log('🟢 ===== LOGIN SUCCESSFUL =====');
      console.log('🟢 Result:', result);
      console.log('🟢 User:', result.user?.email);
      console.log('🟢 Session exists:', !!result.session);
      console.log('🟢 ==============================');
      return result;
    } catch (error) {
      console.error('❌ ===== LOGIN FAILED =====');
      console.error('❌ Error:', error);
      console.error('❌ ======================');
      throw error;
    }
  };

  const register = async (email: string, password: string) => {
    console.log('🔵 ===== STARTING REGISTRATION PROCESS =====');
    console.log('🔵 Email:', email);
    console.log('🔵 Password length:', password.length);
    
    try {
      const result = await auth.signUp(email, password);
      console.log('🟢 ===== REGISTRATION SUCCESSFUL =====');
      console.log('🟢 Result:', result);
      console.log('🟢 User:', result.user?.email);
      console.log('🟢 Session exists:', !!result.session);
      console.log('🟢 Needs confirmation:', !result.session && result.user);
      console.log('🟢 ==============================');
      return result;
    } catch (error) {
      console.error('❌ ===== REGISTRATION FAILED =====');
      console.error('❌ Error:', error);
      console.error('❌ =========================');
      throw error;
    }
  };

  const logout = async () => {
    await auth.signOut();
  };

  const loginWithGoogle = async () => {
    // Always use production URL for consistency
    const redirectUrl = 'https://voice-task-frontend.vercel.app';
    
    console.log('Google OAuth redirect URL:', redirectUrl);
    console.log('Current origin:', window.location.origin);
    
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectUrl,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent'
        }
      }
    });
    if (error) throw error;
    return data;
  };

  useEffect(() => {
    console.log('🔧 AuthContext: Initializing Supabase auth...');
    console.log('🔧 Supabase URL:', supabaseUrl);
    console.log('🔧 Supabase Anon Key:', supabaseAnonKey ? 'Present' : 'Missing');
    console.log('🔧 Current URL:', window.location.href);
    
    // Handle auth callback from URL
    const handleAuthCallback = async () => {
      console.log('🔄 Getting initial session...');
      const { data, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('❌ Error getting session:', error);
        console.error('❌ Error details:', error.message, error.status);
      } else {
        console.log('📊 Initial session data:', data);
        
        if (data.session) {
          console.log('🟢 Found existing session, user:', data.session.user?.email);
          setSession(data.session);
          setCurrentUser(data.session.user);
          
          // Clean up the URL by removing the auth code
          if (window.location.search.includes('code=')) {
            console.log('🗑️ Cleaning up auth code from URL');
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        } else {
          console.log('🔴 No existing session found');
        }
      }
      
      console.log('💯 Setting loading to false');
      setLoading(false);
    };

    handleAuthCallback();

    // Listen for auth changes
    console.log('🔊 Setting up auth state change listener...');
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('🔄 ===== AUTH STATE CHANGE =====');
        console.log('🔄 Event:', event);
        console.log('🔄 Session exists:', !!session);
        console.log('🔄 User exists:', !!session?.user);
        console.log('🔄 User email:', session?.user?.email);
        console.log('🔄 User ID:', session?.user?.id);
        console.log('🔄 Session expires at:', session?.expires_at);
        
        setSession(session);
        setCurrentUser(session?.user ?? null);
        setLoading(false);
        
        if (session?.user) {
          console.log('🟢 ✅ User authenticated - should navigate to main app');
          console.log('🟢 Current user set to:', session.user.email);
        } else {
          console.log('🔴 ❌ User not authenticated - should show landing page');
          console.log('🔴 Current user set to: null');
        }
        
        console.log('🔄 ===========================');
      }
    );

    return () => {
      console.log('🧽 Cleaning up auth subscription');
      subscription.unsubscribe();
    };
  }, []);

  const value = {
    currentUser,
    session,
    login,
    register,
    logout,
    loginWithGoogle,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
